import React, { useState, useCallback, useMemo } from 'react';
import { AttachmentCarousel } from '../components/AttachmentCarousel/AttachmentCarousel';
import type { SavedAttachment } from '../types/attachments';

export interface UseViewerAttachmentCarouselResult {
  hasAttachments: boolean;
  attachmentCount: number;
  openCarousel: (e: React.MouseEvent) => void;
  carouselElement: React.ReactNode;
}

/** Guard: keep only File or SavedAttachment-like (id + file or url). Invalid items are dropped. */
function isValidAttachment(a: unknown): a is SavedAttachment | File {
  if (a instanceof File) return true;
  if (typeof a !== 'object' || a === null) return false;
  const o = a as Record<string, unknown>;
  if (typeof o.id !== 'string') return false;
  if (o.file instanceof File) return true;
  if (typeof o.url === 'string') return true;
  return false;
}

/** Normalize File[] or SavedAttachment[] to SavedAttachment[] (add id from index when item is File). Invalid items filtered out. */
function toSavedAttachments(
  attachments: (SavedAttachment | File)[] | undefined
): SavedAttachment[] {
  if (!attachments?.length) return [];
  const valid = attachments.filter(isValidAttachment);
  return valid.map((a, i) =>
    a instanceof File ? { id: String(i), file: a } : a
  );
}

/**
 * Shared hook for "view only" attachment carousel (e.g. on a saved comment).
 * Encapsulates open/close state and carousel render so callers don't duplicate
 * the same logic (ReplyInput uses useCommentFormFileUpload for its carousel).
 * Accepts File[] or SavedAttachment[]; normalizes to SavedAttachment[] internally.
 */
export function useViewerAttachmentCarousel(
  attachments: (SavedAttachment | File)[] | undefined
): UseViewerAttachmentCarouselResult {
  const [carouselOpen, setCarouselOpen] = useState(false);
  const items = useMemo(() => toSavedAttachments(attachments), [attachments]);

  const openCarousel = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCarouselOpen(true);
  }, []);

  const closeCarousel = useCallback(() => setCarouselOpen(false), []);

  const carouselElement =
    carouselOpen && items.length > 0 ? (
      <AttachmentCarousel
        attachments={items}
        initialIndex={0}
        onClose={closeCarousel}
      />
    ) : null;

  return {
    hasAttachments: items.length > 0,
    attachmentCount: items.length,
    openCarousel,
    carouselElement
  };
}
